import * as checkoutApi from '../../services/checkout-api.js';
import * as formInput from '../../services/form-input.js';
import * as orderSubmission from '../../services/order-submission.js';
import * as stripe from '../../services/stripe.js';

export default () => ({
    checkout: {
        failed: { data: { messages: [], next_url: null, status: '' }, status: false },
        form: null,
        placementStage: 'placing',
        ready: true,
        session: null,
        stripe: { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null },
        termsAccepted: false,
        total: { formatted: '', value: 0 },
    },

    contact: {
        email: '',
        firstName: '',
        lastName: '',
        telephone: '',
        title: '',
    },

    extensionOptionId: null,
    extensionOptions: [],

    order: { placed: false },

    init() {
        const gifted = window.Gifted;

        this.checkout.form = this.$root.querySelector('[name="ExtensionForm"]');
        this.checkout.session = gifted.checkoutSession;
        this.extensionOptions = gifted.extensionOptions;
        this.extensionOptionId = this.extensionOptions[0].id;
        this.checkout.total.value = this.extensionOptions[0].item_cost;
        this.checkout.total.formatted = this.extensionOptions[0].item_cost_formatted;

        stripe.mountElement('#payment-element');

        this.$watch('extensionOptionId', (id) => {
            const option = this.extensionOptions.find(o => o.id === parseInt(id));

            if (option) {
                stripe.updateAmount(option.item_cost);
                this.checkout.total.value = option.item_cost;
                this.checkout.total.formatted = option.item_cost_formatted;
            }
        });

        formInput.init(this.$root);
    },

    async getStripeToken() {
        this.checkout.ready = false;
        this.order.placed = true;
        this.checkout.placementStage = 'placing';

        const { confirmationToken, error } = await stripe.getToken({
            email: this.contact.email,
            name: `${this.contact.firstName} ${this.contact.lastName}`,
            phone: this.contact.telephone,
        });

        if (!confirmationToken || error) {
            this.order.placed = false;
            this.checkout.ready = true;

            if (error && error.type !== 'validation_error') {
                this.checkout.stripe.errorMessage = error.message;
            }

            return;
        }

        this.checkout.stripe.confirmationToken = confirmationToken.id;
        this.checkout.stripe.paymentMethod = confirmationToken.payment_method_preview;
    },

    async submitForm(event) {
        event.preventDefault();

        if (!this.checkout.form.checkValidity()) {
            this.checkout.form.querySelectorAll(':invalid').forEach(el => el.dataset.touched = 'true');
            this.checkout.form.querySelector(':invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        await this.getStripeToken();

        if (!this.checkout.stripe.confirmationToken) return;

        const formData = new FormData(this.checkout.form);

        formData.append('checkout_session_type', this.checkout.session.type);
        formData.append('stripe_token', this.checkout.stripe.confirmationToken);

        if (this.checkout.stripe.paymentIntent) {
            formData.append('payment_intent_id', this.checkout.stripe.paymentIntent);
        }

        if (this.checkout.stripe.paymentMethod) {
            formData.append('stripe_country', this.checkout.stripe.paymentMethod.billing_details?.address?.country || '');
        }

        try {
            const data = await checkoutApi.submit(this.checkout.form, formData);

            this.checkout.placementStage = 'finalising';
            this.awaitOrderCreation(data.session_id);
        } catch (data) {
            if (data.status === 'PAYMENT_REQUIRES_ACTION') {
                const { error, paymentIntent } = await orderSubmission.handlePaymentAction(data.payment.client_secret);

                if (!paymentIntent || error) {
                    this.checkout.stripe = { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null };
                    this.order.placed = false;
                    this.checkout.ready = true;
                    this.checkout.failed = {
                        data: { messages: [error?.message], next_url: null, status: 'PAYMENT_FAILED' },
                        status: true,
                    };
                    return;
                }

                this.checkout.stripe.paymentIntent = paymentIntent.id;

                setTimeout(() => this.submitForm(event), 500);
            } else {
                const action = orderSubmission.decideNextAction(data, { hasPaymentIntent: !!this.checkout.stripe.paymentIntent });

                if (action.type === 'await') {
                    this.checkout.placementStage = 'finalising';
                    this.awaitOrderCreation(action.sessionId || this.checkout.session.id);
                    return;
                }

                this.checkout.ready = true;
                this.checkout.failed = { data, status: true };
            }
        }
    },

    awaitOrderCreation(sessionId) {
        orderSubmission.awaitOrderCreation(sessionId, {
            onComplete: (session) => {
                window.location.pathname = new URL(session.redirect_url).pathname;
            },
            onFailed: (data) => {
                this.order.placed = false;
                this.checkout.failed = { data, status: true };
            },
            onTimeout: () => {
                // Payment succeeded; rest on the 'finalising' reassurance copy.
            },
        });
    },

    removePromoCode() {},

    changeStep() {
        this.order.placed = false;
        this.checkout.failed = { data: { messages: [], next_url: null, status: '' }, status: false };
        this.checkout.ready = true;
    },
});
